/**
 * Printing.
 *
 * The receipt is rendered into a container the print stylesheet keeps visible
 * while hiding everything else (see the @media print block in index.css). That
 * is more reliable across browsers and Electron than an iframe or a popup, and
 * it means the same React components render the on-screen preview and the paper
 * copy - so what the cashier checks is what comes out.
 *
 * In the Electron build `window.posPrinter` is exposed by the preload script and
 * prints silently to the configured thermal printer, with no dialog. In a plain
 * browser it falls back to window.print().
 */

/** Is the app running inside the Electron shell? */
export const isDesktop = () => typeof window !== 'undefined' && !!window.posPrinter;

/**
 * Tell the browser's own print dialog what paper size this job needs.
 *
 * The Electron path sets an exact page size through the OS print API, with no
 * dialog to second-guess it. A plain browser has no such API - `window.print()`
 * hands control to the OS dialog, and that dialog's own margin and paper-size
 * choice sit on top of whatever this page's CSS says, which is why narrowing
 * the receipt's own width alone was not enough. An `@page { size }` rule is
 * the one lever a web page still has: Chrome and Edge read it and offer (often
 * pre-select) a matching custom paper size, rather than leaving the roll width
 * to whatever the dialog happened to remember from the last print - a
 * spreadsheet, a PDF, anything.
 *
 * It still has to be paired with a person setting Margins to *None* in that
 * dialog - CSS cannot reach into the dialog and change that toggle - so this
 * narrows what can go wrong, it does not remove the dialog as a variable.
 */
const setPrintPageSize = (widthMM) => {
    const style = document.createElement('style');
    style.id = 'pos-print-page-size';
    style.textContent = `@page { size: ${widthMM}mm auto; margin: 0; }`;
    document.head.appendChild(style);
    return () => style.remove();
};

/**
 * Print whatever is currently in #print-root.
 *
 * @param {object} [options]
 * @param {string} [options.printerName]  a specific printer; the default otherwise
 * @param {number} [options.copies]
 * @param {number} [options.widthMM]      80 or 58
 */
export const printCurrent = async ({ printerName, copies = 1, widthMM = 80 } = {}) => {
    // Let React commit the receipt into #print-root before the print starts.
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    if (isDesktop()) {
        return window.posPrinter.print({ printerName, copies, widthMM });
    }

    const removePageSize = setPrintPageSize(widthMM);
    try {
        for (let copy = 0; copy < copies; copy += 1) {
            window.print();
        }
    } finally {
        // window.print() blocks until the dialog closes, so the rule has done
        // its job by the time execution reaches here - remove it rather than
        // leaving a stale width behind for the next unrelated print (Ctrl+P on
        // a report screen, say).
        removePageSize();
    }
    return { ok: true, fallback: true };
};

/**
 * Kick the cash drawer.
 *
 * The drawer is wired to the receipt printer and opens on an ESC/POS pulse,
 * which only the desktop build can send. In a browser this is a no-op rather
 * than an error - the till still works, the drawer is just opened by hand.
 */
export const openCashDrawer = async ({ printerName } = {}) => {
    if (!isDesktop()) return { ok: false, reason: 'not-desktop' };
    return window.posPrinter.openDrawer({ printerName });
};

/** Printers the machine knows about, for the settings screen. */
export const listPrinters = async () => {
    if (!isDesktop()) return [];
    return window.posPrinter.list();
};

/**
 * Full-screen toggle. A POS runs full screen so a stray tap cannot reveal the
 * desktop behind it.
 */
export const toggleFullscreen = async () => {
    if (isDesktop() && window.posShell?.toggleFullscreen) {
        return window.posShell.toggleFullscreen();
    }

    if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen?.().catch(() => {});
        return true;
    }
    await document.exitFullscreen?.().catch(() => {});
    return false;
};
